import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyObject } from "../../RadObject";
import { Accordion } from "./Accordion";
import { AccordionPanel } from "./AccordionPanel";
import { ButtonStyleEnum } from "./ButtonStyleEnum";
import { Dialog, DialogPropertyValues, IDialog } from "./Dialog";
import { TextBox, TextBoxTypeEnum } from "./TextBox";
import { NAMESPACE } from "./_Namespace";

import "./ExceptionDetailDialog.scss";

export type ExceptionDetailDialogPropertyValues =
    DialogPropertyValues
    & {
        exceptionDetail: any;
    };

export type ExceptionDetailDialogProperties = {
    [name in keyof ExceptionDetailDialogPropertyValues]: Property<ExceptionDetailDialogPropertyValues[name]>
};

export interface IExceptionDetailDialog extends IDialog, ExceptionDetailDialogProperties
{
}

export class ExceptionDetailDialog extends Dialog implements IExceptionDetailDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ExceptionDetailDialog";
    public static override className = "ExceptionDetailDialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ExceptionDetailDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                buttonStyle: ButtonStyleEnum.okOnly,
                title: "Exception Detail",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected accordion?: Accordion;

    // #endregion

    // #region Properties

    public exceptionDetail!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ExceptionDetailDialogPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-exception-detail-dialog");

        // Trigger Property Changes
        this.exceptionDetail.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _exceptionDetailChanged(change: PropertyChange<any>)
    {
        this._updateDisplay();
    }
    protected _updateDisplay()
    {
        let
            exceptionDetail = this.exceptionDetail.get();

        Assert.isNotNullOrUndefined(this.accordion, "this.accordion");

        this.accordion.controlsClear();

        while (exceptionDetail)
        {
            const
                accordionPanel =
                    new AccordionPanel({ text: exceptionDetail.type })
                        .appendTo(this.accordion);

            const
                textBoxMessage = new TextBox({ cssClass: "rad-message", enabled: false, type: TextBoxTypeEnum.multiline, value: exceptionDetail.message }),
                $$textBoxMessage = textBoxMessage.$$element.get();

            Assert.isNotNullOrUndefined($$textBoxMessage, "$$textBoxRadMessage");

            accordionPanel
                .append(
                    JNativeStatic.create$$({
                        nodeName: "p",
                        children: [
                            {
                                nodeName: "strong",
                                text: "Message:"
                            },
                            {
                                nodeName: "br",
                            },
                            $$textBoxMessage[0]
                        ]
                    })
                );

            const
                textBoxStackTrace = new TextBox({ cssClass: "rad-stack-trace", enabled: false, type: TextBoxTypeEnum.multiline, value: exceptionDetail.stackTrace }),
                $$textBoxStackTrace = textBoxStackTrace.$$element.get();

            Assert.isNotNullOrUndefined($$textBoxStackTrace, "$$textBoxStackTrace");

            accordionPanel
                .append(
                    JNativeStatic.create$$({
                        nodeName: "p",
                        children: [
                            {
                                nodeName: "strong",
                                text: "Stack Trace:"
                            },
                            {
                                nodeName: "br",
                            },
                            $$textBoxStackTrace[0]
                        ]
                    })
                );

            exceptionDetail = exceptionDetail.innerExceptionDetail;
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ExceptionDetailDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.exceptionDetail = new PropertyObject(this, propertyValues.exceptionDetail, this._exceptionDetailChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.accordion =
            new Accordion()
                .appendTo(this);

        return $$element;
    }
    public override openAsync(exceptionDetail: any)
    {
        this.exceptionDetail.set(exceptionDetail);
        return super.openAsync();
    }

    // #endregion

    // #endregion
}

